import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import { LoaderFunctionArgs, redirect } from "react-router-dom";
import { apiClient, AmenityTableLoaderResult } from "~/services/apiClient";
import { LoaderData, PrecomputedAmenityData, Reservation } from "~/types";
import { getCachedAuthAccessInfo, loadCookiesData, parseDayTime } from "~/utils";
import {
  createClientFilteredShouldRevalidate,
  setLoaderQueryData,
  STALE_TIME_TIER_A,
} from "~/utils/loaderQueryCache";
import * as APITypes from "~/API";
import { getFilteredAmenityList } from "~/utils/filters/getFilteredAmenityList";
import { mergeAvailability } from "~/utils/amenity/amenityAvailability";
import dayjs from "dayjs";

export type AmenityTablePageLoaderType = {
  amenityList: LoaderData<PrecomputedAmenityData[]>;
  propertyList: APITypes.Property[];
  isAdminOrSuperAdmin: boolean;
};

export function amenityTableQueryKey(
  ownerID: string,
  ownerType: APITypes.OwnerType,
) {
  return ["amenity-table", ownerID, ownerType] as const;
}

export const amenityTableShouldRevalidate = createClientFilteredShouldRevalidate(
  (owner) => amenityTableQueryKey(owner.ownerID, owner.ownerType),
  STALE_TIME_TIER_A,
);

export async function fetchAmenityTableData(): Promise<
  AmenityTablePageLoaderType | Response
> {
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");

  const authAccessInfo = await loadCookiesData(user.uid);
  if (!authAccessInfo.success) return redirect("/explore");

  const { ownerID, ownerType, role } = authAccessInfo.data;
  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  const result = await apiClient.loader<AmenityTableLoaderResult>(
    "amenity-table",
    { ownerID, ownerType, userID: user.uid },
  );

  if (!result.success || !result.data) {
    console.error("Failed to load amenities:", result.error);
    return redirect("/explore");
  }

  const rawAmenityList = result.data.amenityList ?? [];
  const formattedAmenityList: PrecomputedAmenityData[] = rawAmenityList.map(
    (item) => {
      const reservations: Reservation[] = item.reservations.map((r) => ({
        timeFrame: {
          start: parseDayTime(r.reservation.startDate),
          end: parseDayTime(r.reservation.endDate),
        },
        tenantName: r.tenantName ?? "",
        createdAt: dayjs(r.reservation.createdAt),
        data: r.reservation,
      }));
      const formattedAmenityDetails = {
        amenityDetails: item.amenityDetails,
        propertyDetails: item.propertyDetails,
        buildingDetails: item.buildingDetails ?? undefined,
        reservations,
        accessRights: item.accessRights ?? undefined,
      };
      const availability = mergeAvailability(formattedAmenityDetails);
      return {
        formattedAmenityDetails,
        availability,
      };
    },
  );

  const filteredAmenityList = getFilteredAmenityList(
    formattedAmenityList,
    isAdminOrSuperAdmin,
  );

  return {
    amenityList: {
      success: true,
      data: filteredAmenityList,
    },
    propertyList: result.data.propertyList ?? [],
    isAdminOrSuperAdmin,
  };
}

export async function AmenityTablePageLoader({
  request,
}: LoaderFunctionArgs): Promise<AmenityTablePageLoaderType | Response> {
  void request;
  const data = await fetchAmenityTableData();
  if (data instanceof Response) {
    return data;
  }

  const auth = getCachedAuthAccessInfo();
  if (auth) {
    setLoaderQueryData(
      amenityTableQueryKey(auth.ownerID, auth.ownerType),
      data,
      STALE_TIME_TIER_A,
    );
  }

  return data;
}
